import {
    DISCUSSIONS_LAUNCHED,
    type Discussion,
    type DiscussionComment,
    type DiscussionList,
    type DiscussionPage,
    type DiscussionSort,
    type DiscussionTopicId,
    type DiscussionViewerState,
    type DiscussionVoteResult
} from '#shared/types/Discussion'

/**
 * Client for the public discussion boards (`server/api/discussions`).
 * Reads are public and SSR-safe; every write sends the user's Firebase ID token.
 */
export function useDiscussions() {
    const { authFetch } = useAuthFetch()

    function endpoint(slug: string, suffix = '') {
        return `/api/discussions/${encodeURIComponent(slug)}${suffix}`
    }

    async function listDiscussions(params: {
        topic?: DiscussionTopicId
        sort: DiscussionSort
        cursor?: string
        limit?: number
    }): Promise<DiscussionList> {
        return await $fetch<DiscussionList>('/api/discussions', { query: params })
    }

    async function getDiscussion(slug: string): Promise<DiscussionPage> {
        return await $fetch<DiscussionPage>(endpoint(slug))
    }

    async function getViewerState(slug: string): Promise<DiscussionViewerState> {
        return await authFetch<DiscussionViewerState>(endpoint(slug, '/viewer'))
    }

    async function createDiscussion(input: {
        topic: DiscussionTopicId
        title: string
        body: string
    }): Promise<{ slug: string; topic: DiscussionTopicId }> {
        return await authFetch('/api/discussions', { method: 'POST', body: input })
    }

    async function updateDiscussion(slug: string, input: { title: string; body: string }): Promise<Discussion> {
        return await authFetch<Discussion>(endpoint(slug), { method: 'PATCH', body: input })
    }

    async function deleteDiscussion(slug: string): Promise<void> {
        await authFetch(endpoint(slug), { method: 'DELETE' })
    }

    async function setUpvote(slug: string, upvoted: boolean): Promise<DiscussionVoteResult> {
        return await authFetch<DiscussionVoteResult>(endpoint(slug, '/vote'), { method: 'PUT', body: { upvoted } })
    }

    async function createComment(slug: string, content: string, parentId?: string): Promise<DiscussionComment> {
        return await authFetch<DiscussionComment>(endpoint(slug, '/comments'), {
            method: 'POST',
            body: { content, parentId: parentId || null }
        })
    }

    async function updateComment(slug: string, id: string, content: string): Promise<DiscussionComment> {
        return await authFetch<DiscussionComment>(endpoint(slug, `/comments/${encodeURIComponent(id)}`), {
            method: 'PATCH',
            body: { content }
        })
    }

    async function deleteComment(slug: string, id: string): Promise<void> {
        await authFetch(endpoint(slug, `/comments/${encodeURIComponent(id)}`), { method: 'DELETE' })
    }

    return {
        listDiscussions,
        getDiscussion,
        getViewerState,
        createDiscussion,
        updateDiscussion,
        deleteDiscussion,
        setUpvote,
        createComment,
        updateComment,
        deleteComment
    }
}

/** Keeps the boards out of search results until `DISCUSSIONS_LAUNCHED`. */
export function useDiscussionsSoftLaunch() {
    if (!DISCUSSIONS_LAUNCHED) useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] })
}

export function discussionPath(discussion: { topic: DiscussionTopicId; slug: string }): string {
    return `/diskusie/${discussion.topic}/${discussion.slug}`
}

/** Turns an API failure into a message for the user. */
export function discussionErrorMessage(error: unknown): string {
    const failure = error as { statusCode?: number; status?: number; data?: { message?: string } }
    const status = failure.statusCode || failure.status
    const message = failure.data?.message || ''
    if (status === 401) return 'Pre túto akciu sa prihlás znova.'
    if (status === 403 && message.includes('profile')) return 'Pred písaním si najprv dokonči profil.'
    if (status === 403) return 'Na túto akciu nemáš oprávnenie.'
    if (status === 404) return 'Diskusia alebo komentár už neexistuje. Obnov stránku.'
    if (status === 429 && message.includes('per day')) return 'Dnes si už založil(a) 5 diskusií. Ďalšiu môžeš zajtra.'
    if (status === 429) return 'Píšeš príliš rýchlo, chvíľku počkaj a skús to znova.'
    if (status === 400) return 'Skontroluj, či je všetko správne vyplnené.'
    return 'Akciu sa nepodarilo dokončiť. Skús to znova.'
}
